import React from 'react';

import type { MenuItem } from '@atlaskit/editor-common/extensions';
import AppsIcon from '@atlaskit/icon/core/apps';

type ExtensionIcon = React.ComponentType<{ label: string }>;

const lazyExtensionIconCache = new WeakMap<NonNullable<MenuItem['icon']>, ExtensionIcon>();

const resolveExtensionIcon = async (getIcon: MenuItem['icon']): Promise<ExtensionIcon> => {
	try {
		const maybeIcon = await getIcon?.();
		if (!maybeIcon) {
			return AppsIcon;
		}
		if (Object.prototype.hasOwnProperty.call(maybeIcon, 'default')) {
			return (maybeIcon as { default?: ExtensionIcon }).default ?? AppsIcon;
		}
		return maybeIcon as ExtensionIcon;
	} catch {
		return AppsIcon;
	}
};

const createLazyExtensionIcon = (getIcon: NonNullable<MenuItem['icon']>): ExtensionIcon => {
	const cachedIcon = lazyExtensionIconCache.get(getIcon);
	if (cachedIcon) {
		return cachedIcon;
	}

	const LazyIcon = React.lazy(() =>
		resolveExtensionIcon(getIcon).then((Icon) => ({ default: Icon })),
	) as React.NamedExoticComponent<React.ComponentProps<ExtensionIcon>>;
	LazyIcon.displayName = 'lazy(ExtensionIcon)';

	const Icon: ExtensionIcon = (props) => (
		<React.Suspense fallback={<AppsIcon label={props.label} />}>
			<LazyIcon label={props.label} />
		</React.Suspense>
	);

	lazyExtensionIconCache.set(getIcon, Icon);
	return Icon;
};

export const ExtensionQuickInsertIcon = ({
	getIcon,
	label,
}: {
	getIcon?: MenuItem['icon'];
	label: string;
}): React.JSX.Element => {
	const Icon = React.useMemo(
		() => (getIcon ? createLazyExtensionIcon(getIcon) : AppsIcon),
		[getIcon],
	);
	return <Icon label={label} />;
};
